import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { Swiper, SwiperSlide } from 'swiper/react';
import React, {
    useEffect,
    useRef,
    useState,
    createContext,
    useContext,
    useMemo,
} from 'react';

import { useKeyboard } from '$hooks/events.js';
import Button from '$partials/ui/button.jsx';
import Media from '$partials/media/media.jsx';
import IconClose from '$partials/icons/icon-close';
import SimpleImage from '$partials/media/image-simple.jsx';


/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

/** @type {React.Context<import('$types/components/partials/media.d.ts').MediaGalleryContext>} */

const MediaGalleryContext = createContext({
    galleryInitialId: null,
    setGalleryInitialId: () => {}
});

/**
 * @hook useMediaGallery
 */
/**
 * @type {import('$types/components/partials/media.d.ts').useMediaGallery}
 */

const useMediaGallery = () => {
    const context = useContext(MediaGalleryContext);

    if (context === undefined) {
        throw new Error('useMediaGallery must be used within a MediaGalleryProvider.');
    }

    return context;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/media.d.ts').MediaGalleryProvider}
 */
const MediaGalleryProvider = ({ children }) => {
    const [galleryInitialId, setGalleryInitialId] = useState(null);

    // render
    return (
        <MediaGalleryContext.Provider value={useMemo(() => {
            return {
                galleryInitialId,
                setGalleryInitialId
            };
        }, [
            galleryInitialId,
            setGalleryInitialId
        ])}>
            {children}
        </MediaGalleryContext.Provider>
    );
};

/**
 * @type {import('$types/components/partials/media.d.ts').MediaGallery}
 */

const MediaGallery = ({ items, initialId }) => {
    const [swiper, setSwiper] = useState();
    const initialIndex = useMemo(() => {
        if (initialId === null || initialId === undefined) {return 0;}
        const index = items?.findIndex(item => item.id === initialId);
        return index >= 0 ? index : 0;
    }, [initialId, items]);
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const dialogRef = useRef();
    const scrollElementRef = useRef();
    const ctx = useMediaGallery();

    useKeyboard({
        'Escape': (event) => {
            // Prevent dialog's default behaviour
            event.preventDefault();
            ctx.setGalleryInitialId(null);
        },
        'ArrowLeft': () => {
            swiper?.slidePrev?.(0);
        },
        'ArrowRight': () => {
            swiper?.slideNext?.(0);
        }
    }, []);

    useEffect(() => {
        // Sync currentIndex when initialIndex changes
        if (initialIndex !== null && initialIndex !== undefined) {
            setCurrentIndex(initialIndex);
        }
    }, [initialIndex]);

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        const scrollElementNode = scrollElementRef.current;
        if (dialogNode && !dialogNode.open && scrollElementNode) {
            disableBodyScroll(scrollElementNode);
            dialogNode.showModal?.();
        }

        return () => {
            enableBodyScroll(scrollElementNode);
            dialogNode.close?.();
        };
    }, []);

    const renderedItems = items?.filter(itm => (itm.media?.image || itm.media?.video || itm.media?.videoEmbed));

    return (
        <>
            <dialog
                className={'media-gallery dialog-reset text-charcoal theme-light'}
                ref={dialogRef}>
                <div
                    className={'w-full fixed top-0 bottom-0 left-0 h-dvh max-h-dvh bg-main-background flex flex-col'}
                    ref={scrollElementRef}>

                    {/* Desktop Close */}
                    <div className='block absolute z-20 top-24 right-24'>
                        <Button
                            className='flex select-none h-[40px] w-[40px] rounded justify-center items-center button-secondary-filled'
                            onClick={() => ctx.setGalleryInitialId(null)}>
                            <IconClose/>
                        </Button>
                    </div>

                    {/* Slide counter */}
                    {renderedItems?.length > 0 && (
                        <div className='relative md:absolute z-20 mb-42 top-16 left-16 md:top-24 md:left-24'>
                            <span className='text-body-r'>{currentIndex + 1} / {renderedItems.length}</span>
                        </div>
                    )}
                    <div className='flex-1 flex flex-col h-full py-24'>
                        {/* Carousel gallery */}
                        <Swiper
                            className='grow'
                            grabCursor={true}
                            initialSlide={initialIndex}
                            onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
                            onSwiper={setSwiper}
                            simulateTouch={true}
                            slidesPerView={1}
                            spaceBetween={50}>
                            {renderedItems.map((item, idx) => {
                                const video = item.media.video;
                                const videoEmbed = item.media.videoEmbed;

                                const hasVideo = !!video?.src || !!videoEmbed?.src;
                                const isEmbed = !!videoEmbed?.src;
                                return (
                                    <SwiperSlide
                                        aria-roledescription='slide'
                                        className='h-full select-none px-16 sm:px-24'
                                        key={idx}
                                        role='group'>
                                        {hasVideo ? (
                                            <div className='h-full w-full flex justify-center items-center'>
                                                <div className='aspect-video sm:aspect-auto w-full md:max-w-[80%]'>
                                                    {isEmbed ? (
                                                        <Media
                                                            videoEmbed={{
                                                                ...item.media.videoEmbed,
                                                                className: 'h-full w-full max-h-[600px] object-cover',
                                                                playButtonPosition: 'center',
                                                            }}/>
                                                    ) : (
                                                        <Media
                                                            video={{
                                                                ...item.media.video,
                                                                className: 'h-full w-full max-h-[600px] object-cover',
                                                                playButtonPosition: 'center'
                                                            }}/>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className='h-full w-full flex justify-center items-center'>
                                                <Media
                                                    image={{
                                                        ...item.media.image,
                                                        containerClassName: 'sm:h-full w-auto object-cover aspect-video sm:aspect-auto md:max-w-[80%]',
                                                    }}/>
                                            </div>
                                        )}
                                    </SwiperSlide>
                                );
                            })
                            }
                        </Swiper>


                        {/* Thumbnails */}
                        {renderedItems?.length > 1 && (
                            <div className='shrink-0 overflow-x-auto no-scrollbar'>
                                <div className='mx-auto flex w-max gap-10 px-16 sm:px-24 md:px-40 pt-80 md:pt-10'>
                                    {renderedItems.map((item, index) => {
                                        const video = item.media.video;
                                        const videoEmbed = item.media.videoEmbed;

                                        const hasVideo = !!video?.src || !!videoEmbed?.src;
                                        const isEmbed = !!videoEmbed?.src;
                                        const isActive = currentIndex === index;
                                        return (
                                            <button
                                                aria-label={`Go to slide ${index + 1}`}
                                                className={`
                                                flex-shrink-0
                                                transition-opacity
                                                ${isActive ? 'opacity-100' : 'opacity-40 hover:opacity-60'}
                                                `}
                                                key={index}
                                                onClick={() => {
                                                    if (swiper) {
                                                        swiper.slideTo(index);
                                                    }
                                                }}
                                                type='button'>
                                                {hasVideo ? (
                                                    <div className='h-40 w-auto overflow-hidden'>
                                                        {isEmbed ? (
                                                            <SimpleImage
                                                                className='h-full w-full object-cover'
                                                                src={videoEmbed?.poster?.url}/>
                                                        ) : (
                                                            <SimpleImage
                                                                className='h-full w-full object-cover'
                                                                src={video?.poster?.url}/>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className='h-40 aspect-video overflow-hidden'>
                                                        <SimpleImage
                                                            className='h-full w-full object-cover'
                                                            {...item.media?.image}/>
                                                    </div>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </dialog>
        </>
    );
};

export default MediaGallery;

export {
    MediaGalleryProvider,
    MediaGalleryContext,
    useMediaGallery
};
